import React, { useEffect, useState } from 'react';
import {
  createCustomerUser,
  deleteCustomerUser,
  fetchAuditLogs,
  fetchCustomerSummary,
  fetchCustomerUsers,
  forceLogout,
  updateCustomerUser,
} from '../services/api';
import CredentialsModal from './CredentialsModal';

const defaultUserForm = {
  username: '',
  email: '',
  display_name: '',
  status: 'active',
};

function CustomerAdminDashboard({ session, onLogout }) {
  const token = session?.access;
  const customerId = session?.profile?.customer_id || session?.customer_id;
  const [customer, setCustomer] = useState(null);
  const [users, setUsers] = useState([]);
  const [userForm, setUserForm] = useState(defaultUserForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [auditLogs, setAuditLogs] = useState([]);
  const [credentialsModal, setCredentialsModal] = useState({
    isOpen: false,
    username: '',
    password: '',
    title: 'Credentials Generated',
  });

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const loadCustomer = async () => {
    try {
      const data = await fetchCustomerSummary(token);
      setCustomer(data);
    } catch (err) {
      setError(err.message || 'Unable to load customer summary.');
    }
  };

  const loadUsers = async () => {
    try {
      if (!customerId) {
        setUsers([]);
        return;
      }
      const data = await fetchCustomerUsers(token, customerId);
      setUsers(data);
    } catch (err) {
      setError(err.message || 'Unable to load users.');
    }
  };

  const loadAudit = async () => {
    try {
      const data = await fetchAuditLogs(token);
      setAuditLogs(data);
    } catch (err) {
      console.warn('Failed to load audit logs', err);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError('');
      await Promise.all([loadCustomer(), loadUsers(), loadAudit()]);
      setLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setUserForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const data = await createCustomerUser(token, customerId, userForm);
      await loadUsers();
      
      // Show credentials modal
      if (data.temporary_password && data.username) {
        setCredentialsModal({
          isOpen: true,
          username: data.username,
          password: data.temporary_password,
          title: 'Customer User Credentials',
        });
      } else {
        showSuccess('User created.');
      }
      
      setUserForm(defaultUserForm);
      await loadCustomer();
    } catch (err) {
      const detail =
        err?.payload?.non_field_errors?.[0] ||
        err?.payload?.status?.[0] ||
        err.message ||
        'Failed to create user.';
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUserStatus = async (userId, status) => {
    setError('');
    try {
      await updateCustomerUser(token, customerId, userId, { status });
      await loadUsers();
      await loadCustomer();
      showSuccess('User status updated.');
    } catch (err) {
      const detail =
        err?.payload?.status?.[0] || err.message || 'Failed to update user status.';
      setError(detail);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    setError('');
    try {
      await deleteCustomerUser(token, customerId, userId);
      await loadUsers();
      await loadCustomer();
      showSuccess('User deleted.');
    } catch (err) {
      setError(err.message || 'Failed to delete user.');
    }
  };

  const handleForceLogout = async (userId) => {
    try {
      await forceLogout(token, userId);
      showSuccess('User sessions revoked.');
    } catch (err) {
      setError(err.message || 'Failed to force logout.');
    }
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div>
          <h1>Customer Admin Console</h1>
          <p>Welcome back, {session?.display_name || session?.profile?.display_name}</p>
        </div>
        <button className="outline-button" onClick={onLogout}>
          Logout
        </button>
      </header>

      {error && <div className="alert error">{error}</div>}
      {successMessage && <div className="alert success">{successMessage}</div>}

      {loading ? (
        <p>Loading dashboard...</p>
      ) : (
        <>
          <section className="dashboard-section">
            <div className="card">
              <h2>{customer?.name}</h2>
              <div className="metrics">
                <div>
                  <span>Entitlement</span>
                  <strong>{customer?.entitlement_usage?.allowed ?? 0}</strong>
                </div>
                <div>
                  <span>Active Users</span>
                  <strong>{customer?.entitlement_usage?.active ?? 0}</strong>
                </div>
                <div>
                  <span>Available</span>
                  <strong>{customer?.entitlement_usage?.available ?? 0}</strong>
                </div>
              </div>
              <div className="grid-two" style={{ gap: '1.25rem', marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid var(--border-color)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</span>
                  <strong className={`status-${customer?.status}`} style={{ fontSize: '1rem', fontWeight: 600 }}>{customer?.status}</strong>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Expiry</span>
                  <strong style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-main)' }}>{customer?.expiry_date || '—'}</strong>
                </div>
              </div>
            </div>
          </section>

          <section className="dashboard-section">
            <div className="card">
              <h3>Create Customer User</h3>
              <form className="stacked-form" onSubmit={handleCreateUser}>
                <div className="grid-two">
                  <label>
                    Username
                    <input
                      required
                      name="username"
                      value={userForm.username}
                      onChange={handleChange}
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      name="email"
                      value={userForm.email}
                      onChange={handleChange}
                    />
                  </label>
                </div>
                <label>
                  Display Name
                  <input
                    name="display_name"
                    value={userForm.display_name}
                    onChange={handleChange}
                  />
                </label>
                <label>
                  Status
                  <select name="status" value={userForm.status} onChange={handleChange}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </label>
                <button className="primary-button" type="submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Create User'}
                </button>
              </form>

              <table className="compact-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Expiry</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>{user.email || '—'}</td>
                      <td className={`status-${user.status}`}>{user.status}</td>
                      <td>{user.expiry_date || '—'}</td>
                      <td>
                        <button
                          className="inline-button"
                          onClick={() =>
                            handleUpdateUserStatus(
                              user.id,
                              user.status === 'active' ? 'inactive' : 'active'
                            )
                          }
                        >
                          {user.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="inline-button"
                          onClick={() => handleForceLogout(user.id)}
                        >
                          Force Logout
                        </button>
                        <button
                          className="inline-button"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="dashboard-section">
            <div className="card">
              <h3>Recent Activity</h3>
              <div className="audit-log">
                {auditLogs.slice(0, 10).map((log) => (
                  <div key={log.id} className="audit-log-item">
                    <div>
                      <strong>{log.action}</strong> on <span>{log.target_type}</span>
                      <small> {new Date(log.timestamp).toLocaleString()}</small>
                    </div>
                    <small>Actor: {log.actor_username || 'System'}</small>
                  </div>
                ))}
                {auditLogs.length === 0 && <p>No recent activity.</p>}
              </div>
            </div>
          </section>
        </>
      )}
      
      <CredentialsModal
        isOpen={credentialsModal.isOpen}
        onClose={() => setCredentialsModal({ ...credentialsModal, isOpen: false })}
        username={credentialsModal.username}
        password={credentialsModal.password}
        title={credentialsModal.title}
      />
    </div>
  );
}

export default CustomerAdminDashboard;

